import { useState } from 'react';
import type { ReprocessMethod, ReviewLabel } from '../api/types';
import { LabelPicker } from './LabelPicker';

interface Props {
  count: number;
  onReprocess: (method: ReprocessMethod) => void;
  /** Un-approve + re-cut. Distinct from onReprocess: a plain forced tier is a no-op
   * on an approved product, because the runner prefers the pinned cutout. */
  onRecut: (method: ReprocessMethod) => void;
  onSendToNeedsReview: () => void;
  /** Disapprove + park for manual handling (upload / 3p API). */
  onManualPark: () => void;
  /** Bulk lock-in: human-approve (operator pass) the whole selection — including
   * items already queued for a re-cut (the leftovers "approve all above" skips). */
  onHumanApprove: () => void;
  onClear: () => void;
  disabled?: boolean;
  /** Review-label controls. The picked label is owned by the gallery so the same
   * choice drives this toolbar AND the "label all above" button at the bottom. */
  labels: ReviewLabel[];
  activeLabel: string;
  onActiveLabelChange: (label: string) => void;
  onApplyLabel: () => void;
  onRemoveLabel: () => void;
}

// Operator-facing "model" choices. Mirrors the enabled tiers in the spec and the
// backend BulkRerenderBody whitelist. snapedit is shown but DISABLED — there is
// no API key wired yet, and snapedit is NOT in feedforge/cutout_cache.py's tier
// whitelist, so a queued snapedit override would fail in the runner.
const TIER_OPTIONS: { value: ReprocessMethod; label: string; disabled?: boolean }[] = [
  { value: 'tier_0', label: 'tier_0 — rembg lite' },
  { value: 'tier_1', label: 'tier_1 — BiRefNet full' },
  { value: 'tier_2', label: 'tier_2 — threshold packshot' },
  { value: 'ben2', label: 'ben2 — matting' },
  { value: 'floodfill', label: 'floodfill — flood-fill' },
  { value: 'snapedit', label: 'snapedit — requires API key', disabled: true },
];

/**
 * Bulk-remediation toolbar for the QA Review tab. Presentational — the gallery
 * owns the selection and wires the callbacks to the reused Cutouts endpoints.
 * Mirrors BulkActionsToolbar's shape. `z-30` escapes the backdrop-blur stacking
 * context created by the QA header so the toolbar isn't painted behind it.
 */
export function QaBulkRemediationToolbar({
  count, onReprocess, onRecut, onSendToNeedsReview, onManualPark, onHumanApprove,
  onClear, disabled, labels, activeLabel, onActiveLabelChange, onApplyLabel,
  onRemoveLabel,
}: Props) {
  // ben2 default: the strongest matting tier, and the one the dark-background halo
  // wave needs. The <select> drives both "Reprocess as" and "Un-approve + re-cut".
  const [method, setMethod] = useState<ReprocessMethod>('ben2');
  if (count === 0) return null;

  return (
    <div
      role="toolbar"
      aria-label="Bulk remediation"
      className="sticky top-0 z-30 bg-ff-forest text-ff-ivory px-4 py-2 rounded-lg flex flex-wrap items-center gap-3 shadow-soft mb-4"
    >
      <span className="font-mono text-sm">{count} selected</span>
      <div className="flex-1" />
      <select
        data-testid="qa-bulk-method-select"
        value={method}
        onChange={(e) => setMethod(e.target.value as ReprocessMethod)}
        className="px-2 py-1 rounded text-ff-forest text-sm"
      >
        {TIER_OPTIONS.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
      </select>
      <button
        type="button"
        onClick={() => onReprocess(method)}
        disabled={disabled}
        className="px-3 py-1 rounded bg-ff-ivory text-ff-forest hover:bg-ff-paper text-sm font-medium disabled:opacity-50"
      >
        Reprocess as
      </button>
      <button
        type="button"
        data-testid="qa-bulk-recut"
        onClick={() => onRecut(method)}
        disabled={disabled}
        title="Removes the human-approved pin, then queues a fresh cut at the chosen
method. Needed because an approved product ignores a forced tier."
        className="px-3 py-1 rounded bg-ff-ivory text-ff-forest hover:bg-ff-paper text-sm font-medium disabled:opacity-50"
      >
        ⟳ Un-approve + re-cut
      </button>
      <button
        type="button"
        data-testid="qa-bulk-manual-park"
        onClick={onManualPark}
        disabled={disabled}
        title="Disapprove and leave for manual handling — upload a cutout yourself or
send it to a 3rd-party API. No automated re-cut is attempted."
        className="px-3 py-1 rounded bg-ff-danger-soft text-ff-danger hover:bg-ff-danger-soft/70 text-sm font-medium disabled:opacity-50"
      >
        ✗ Manual
      </button>
      <button
        type="button"
        onClick={onSendToNeedsReview}
        disabled={disabled}
        className="px-3 py-1 rounded bg-ff-ivory text-ff-forest hover:bg-ff-paper text-sm font-medium disabled:opacity-50"
      >
        Send to needs-review
      </button>
      <button
        type="button"
        onClick={onHumanApprove}
        disabled={disabled}
        className="px-3 py-1 rounded bg-ff-mint text-ff-forest hover:bg-ff-mint/80 text-sm font-medium disabled:opacity-50"
      >
        ✓ Human approve
      </button>
      <span className="w-px h-5 bg-ff-ivory/25" aria-hidden />
      <LabelPicker
        labels={labels}
        value={activeLabel}
        onChange={onActiveLabelChange}
        tone="dark"
        disabled={disabled}
        data-testid="bulk-label-picker"
      />
      <button
        type="button"
        data-testid="bulk-apply-label"
        onClick={onApplyLabel}
        disabled={disabled || !activeLabel.trim()}
        title="Mark the selection as triaged. Purely a progress marker — it changes
nothing about the product or its cutout."
        className="px-3 py-1 rounded bg-ff-ivory text-ff-forest hover:bg-ff-paper text-sm font-medium disabled:opacity-50"
      >
        🏷 Label selected
      </button>
      <button
        type="button"
        data-testid="bulk-remove-label"
        onClick={onRemoveLabel}
        disabled={disabled || !activeLabel.trim()}
        title="Remove this label from the selection"
        className="px-3 py-1 rounded bg-transparent text-ff-ivory border border-ff-ivory/40 hover:bg-ff-ivory/10 text-sm disabled:opacity-50"
      >
        ⚑ Remove label
      </button>
      <button
        type="button"
        onClick={onClear}
        className="px-3 py-1 rounded bg-transparent text-ff-ivory border border-ff-ivory/40 hover:bg-ff-ivory/10 text-sm"
      >
        Clear
      </button>
    </div>
  );
}
